import baseApi from './api';
import { useMemo } from 'react';
import { experienceStorefrontKey } from './storefrontExperience';

const handleError = (error, context) => {
  console.error(`❌ ${context}:`, error.response?.data || error.message);
  throw error;
};

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

// Bound to the editor's storefront for the lifetime of an async save operation.
export const createStorefrontCmsService = (storefrontKey) => {
  const configFor = (config = {}) => ({
    ...config,
    params: {
      ...(config.params || {}),
      ...(storefrontKey ? { storefront: storefrontKey } : {}),
    },
  });
  const api = {
    get: (url, config) => baseApi.get(url, configFor(config)),
    delete: (url, config) => baseApi.delete(url, configFor(config)),
    post: (url, data, config) => baseApi.post(url, data, configFor(config)),
    put: (url, data, config) => baseApi.put(url, data, configFor(config)),
    patch: (url, data, config) => baseApi.patch(url, data, configFor(config)),
  };
// ── Sections ──────────────────────────────────────────────────────────────────
const fetchFbSections = () =>
  api.get('/fb/sections').then(r => r.data).catch(e => handleError(e, 'fetchFbSections'));

const updateFbSection = (id, data) =>
  api.put(`/fb/sections/${id}`, data).then(r => r.data).catch(e => handleError(e, 'updateFbSection'));

const reorderFbSections = (items) =>
  api.patch('/fb/sections/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderFbSections'));

// ── Hero Slides ───────────────────────────────────────────────────────────────
const fetchHeroSlides = () =>
  api.get('/fb/hero/slides').then(r => r.data).catch(e => handleError(e, 'fetchHeroSlides'));

const createHeroSlide = (formData) =>
  api.post('/fb/hero/slides', formData, multipart).then(r => r.data).catch(e => handleError(e, 'createHeroSlide'));

const updateHeroSlide = (id, formData) =>
  api.put(`/fb/hero/slides/${id}`, formData, multipart).then(r => r.data).catch(e => handleError(e, 'updateHeroSlide'));

const deleteHeroSlide = (id) =>
  api.delete(`/fb/hero/slides/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteHeroSlide'));

const reorderHeroSlides = (items) =>
  api.patch('/fb/hero/slides/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderHeroSlides'));

// ── Occasions ─────────────────────────────────────────────────────────────────
const fetchOccasions = () =>
  api.get('/fb/occasions').then(r => r.data).catch(e => handleError(e, 'fetchOccasions'));

const createOccasion = (formData) =>
  api.post('/fb/occasions', formData, multipart).then(r => r.data).catch(e => handleError(e, 'createOccasion'));

const updateOccasion = (id, formData) =>
  api.put(`/fb/occasions/${id}`, formData, multipart).then(r => r.data).catch(e => handleError(e, 'updateOccasion'));

const deleteOccasion = (id) =>
  api.delete(`/fb/occasions/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteOccasion'));

const reorderOccasions = (items) =>
  api.patch('/fb/occasions/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderOccasions'));

// ── Fusion Box Variants ───────────────────────────────────────────────────────
const fetchVariants = () =>
  api.get('/fb/variants').then(r => r.data).catch(e => handleError(e, 'fetchVariants'));

const createVariant = (formData) =>
  api.post('/fb/variants', formData, multipart).then(r => r.data).catch(e => handleError(e, 'createVariant'));

const updateVariant = (id, formData) =>
  api.put(`/fb/variants/${id}`, formData, multipart).then(r => r.data).catch(e => handleError(e, 'updateVariant'));

const deleteVariant = (id) =>
  api.delete(`/fb/variants/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteVariant'));

const reorderVariants = (items) =>
  api.patch('/fb/variants/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderVariants'));

// ── Jain & Swaminarayan ───────────────────────────────────────────────────────
const fetchJainSection = () =>
  api.get('/fb/jain').then(r => r.data).catch(e => handleError(e, 'fetchJainSection'));

const updateJainSection = (formData) =>
  api.put('/fb/jain', formData, multipart).then(r => r.data).catch(e => handleError(e, 'updateJainSection'));

// ── How It Works ──────────────────────────────────────────────────────────────
const fetchSteps = () =>
  api.get('/fb/how-it-works').then(r => r.data).catch(e => handleError(e, 'fetchSteps'));

const createStep = (data) =>
  api.post('/fb/how-it-works', data).then(r => r.data).catch(e => handleError(e, 'createStep'));

const updateStep = (id, data) =>
  api.put(`/fb/how-it-works/${id}`, data).then(r => r.data).catch(e => handleError(e, 'updateStep'));

const deleteStep = (id) =>
  api.delete(`/fb/how-it-works/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteStep'));

const reorderSteps = (items) =>
  api.patch('/fb/how-it-works/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderSteps'));

// ── Gallery ───────────────────────────────────────────────────────────────────
const fetchGallery = () =>
  api.get('/fb/gallery').then(r => r.data).catch(e => handleError(e, 'fetchGallery'));

const createGalleryImage = (formData) =>
  api.post('/fb/gallery', formData, multipart).then(r => r.data).catch(e => handleError(e, 'createGalleryImage'));

const updateGalleryImage = (id, formData) =>
  api.put(`/fb/gallery/${id}`, formData, multipart).then(r => r.data).catch(e => handleError(e, 'updateGalleryImage'));

const deleteGalleryImage = (id) =>
  api.delete(`/fb/gallery/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteGalleryImage'));

const reorderGalleryImages = (items) =>
  api.patch('/fb/gallery/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderGalleryImages'));

// ── UGC Customer Videos ──────────────────────────────────────────────────────
const fetchUgcVideos = () =>
  api.get('/fb/ugc-videos').then(r => r.data).catch(e => handleError(e, 'fetchUgcVideos'));

const createUgcVideo = (formData) =>
  api.post('/fb/ugc-videos', formData, multipart).then(r => r.data).catch(e => handleError(e, 'createUgcVideo'));

const updateUgcVideo = (id, formData) =>
  api.put(`/fb/ugc-videos/${id}`, formData, multipart).then(r => r.data).catch(e => handleError(e, 'updateUgcVideo'));

const deleteUgcVideo = (id) =>
  api.delete(`/fb/ugc-videos/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteUgcVideo'));

const reorderUgcVideos = (items) =>
  api.patch('/fb/ugc-videos/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderUgcVideos'));

// ── Reviews ───────────────────────────────────────────────────────────────────
const fetchReviews = () =>
  api.get('/fb/reviews').then(r => r.data).catch(e => handleError(e, 'fetchReviews'));

const createReview = (formData) =>
  api.post('/fb/reviews', formData, multipart).then(r => r.data).catch(e => handleError(e, 'createReview'));

const updateReview = (id, formData) =>
  api.put(`/fb/reviews/${id}`, formData, multipart).then(r => r.data).catch(e => handleError(e, 'updateReview'));

const deleteReview = (id) =>
  api.delete(`/fb/reviews/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteReview'));

const reorderReviews = (items) =>
  api.patch('/fb/reviews/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderReviews'));

// ── FAQs ──────────────────────────────────────────────────────────────────────
const fetchFaqs = () =>
  api.get('/fb/faqs').then(r => r.data).catch(e => handleError(e, 'fetchFaqs'));

const createFaq = (data) =>
  api.post('/fb/faqs', data).then(r => r.data).catch(e => handleError(e, 'createFaq'));

const updateFaq = (id, data) =>
  api.put(`/fb/faqs/${id}`, data).then(r => r.data).catch(e => handleError(e, 'updateFaq'));

const deleteFaq = (id) =>
  api.delete(`/fb/faqs/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteFaq'));

const reorderFaqs = (items) =>
  api.patch('/fb/faqs/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderFaqs'));

// ── Footer ────────────────────────────────────────────────────────────────────
const fetchFooter = () =>
  api.get('/fb/footer').then(r => r.data).catch(e => handleError(e, 'fetchFooter'));

const updateFooterSettings = (formData) =>
  api.put('/fb/footer/settings', formData, multipart).then(r => r.data).catch(e => handleError(e, 'updateFooterSettings'));

const createFooterLink = (data) =>
  api.post('/fb/footer/links', data).then(r => r.data).catch(e => handleError(e, 'createFooterLink'));

const updateFooterLink = (id, data) =>
  api.put(`/fb/footer/links/${id}`, data).then(r => r.data).catch(e => handleError(e, 'updateFooterLink'));

const deleteFooterLink = (id) =>
  api.delete(`/fb/footer/links/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteFooterLink'));

const reorderFooterLinks = (items) =>
  api.patch('/fb/footer/links/reorder', { items }).then(r => r.data).catch(e => handleError(e, 'reorderFooterLinks'));

const createFooterSocial = (data) =>
  api.post('/fb/footer/socials', data).then(r => r.data).catch(e => handleError(e, 'createFooterSocial'));

const updateFooterSocial = (id, data) =>
  api.put(`/fb/footer/socials/${id}`, data).then(r => r.data).catch(e => handleError(e, 'updateFooterSocial'));

const deleteFooterSocial = (id) =>
  api.delete(`/fb/footer/socials/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteFooterSocial'));

// ── Popup ─────────────────────────────────────────────────────────────────────
const fetchPopup = () =>
  api.get('/fb/popup').then(r => r.data).catch(e => handleError(e, 'fetchPopup'));

const updatePopup = (formData) =>
  api.put('/fb/popup', formData, multipart).then(r => r.data).catch(e => handleError(e, 'updatePopup'));

// ── Page Builder CMS ─────────────────────────────────────────────────────────
const fetchPageBuilderModules = () =>
  api.get('/fb/page-builder/modules').then(r => r.data).catch(e => handleError(e, 'fetchPageBuilderModules'));

const fetchCmsPages = () =>
  api.get('/fb/page-builder/pages').then(r => r.data).catch(e => handleError(e, 'fetchCmsPages'));

const fetchCmsPage = (id) =>
  api.get(`/fb/page-builder/pages/${id}`).then(r => r.data).catch(e => handleError(e, 'fetchCmsPage'));

const createCmsPage = (data) =>
  api.post('/fb/page-builder/pages', data).then(r => r.data).catch(e => handleError(e, 'createCmsPage'));

const updateCmsPage = (id, data) =>
  api.put(`/fb/page-builder/pages/${id}`, data).then(r => r.data).catch(e => handleError(e, 'updateCmsPage'));

const deleteCmsPage = (id) =>
  api.delete(`/fb/page-builder/pages/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteCmsPage'));

const createCmsPageSection = (pageId, data) =>
  api.post(`/fb/page-builder/pages/${pageId}/sections`, data).then(r => r.data).catch(e => handleError(e, 'createCmsPageSection'));

const updateCmsPageSection = (sectionId, data) =>
  api.put(`/fb/page-builder/sections/${sectionId}`, data).then(r => r.data).catch(e => handleError(e, 'updateCmsPageSection'));

const deleteCmsPageSection = (sectionId) =>
  api.delete(`/fb/page-builder/sections/${sectionId}`).then(r => r.data).catch(e => handleError(e, 'deleteCmsPageSection'));

const reorderCmsPageSections = (pageId, items) =>
  api.patch(`/fb/page-builder/pages/${pageId}/sections/reorder`, { items }).then(r => r.data).catch(e => handleError(e, 'reorderCmsPageSections'));

const uploadCmsImage = (file) => {
  const formData = new FormData();
  formData.append('image', file);
  return api.post('/fb/page-builder/upload', formData, multipart).then(r => r.data).catch(e => handleError(e, 'uploadCmsImage'));
};

const uploadCmsVideo = (file) => {
  const formData = new FormData();
  formData.append('video', file);
  return api.post('/fb/page-builder/upload/video', formData, multipart).then(r => r.data).catch(e => handleError(e, 'uploadCmsVideo'));
};

// field is one of: desktop_video | mobile_video | desktop_poster | mobile_poster
const uploadVideoHeroMedia = (field, file) => {
  const formData = new FormData();
  formData.append(field, file);
  return api.post('/fb/page-builder/upload/video-hero', formData, multipart).then(r => r.data).catch(e => handleError(e, 'uploadVideoHeroMedia'));
};

// Dashboard-session-only CMS preview: mints a short-lived, staff-scoped token
// (never a shareable public link) consumed by the preview iframe's URL.
const createPreviewSession = () =>
  api.post('/fb/page-builder/preview-session').then(r => r.data).catch(e => handleError(e, 'createPreviewSession'));

// ── Blogs CMS ────────────────────────────────────────────────────────────────
const fetchBlogs = () =>
  api.get('/fb/blogs').then(r => r.data).catch(e => handleError(e, 'fetchBlogs'));

const createBlog = (formData) =>
  api.post('/fb/blogs', formData, multipart).then(r => r.data).catch(e => handleError(e, 'createBlog'));

const updateBlog = (id, formData) =>
  api.put(`/fb/blogs/${id}`, formData, multipart).then(r => r.data).catch(e => handleError(e, 'updateBlog'));

const deleteBlog = (id) =>
  api.delete(`/fb/blogs/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteBlog'));

// ── Advanced Popups CMS ──────────────────────────────────────────────────────
const fetchPopups = () =>
  api.get('/fb/popups').then(r => r.data).catch(e => handleError(e, 'fetchPopups'));

const createPopup = (formData) =>
  api.post('/fb/popups', formData, multipart).then(r => r.data).catch(e => handleError(e, 'createPopup'));

const updatePopupById = (id, formData) =>
  api.put(`/fb/popups/${id}`, formData, multipart).then(r => r.data).catch(e => handleError(e, 'updatePopupById'));

const deletePopupById = (id) =>
  api.delete(`/fb/popups/${id}`).then(r => r.data).catch(e => handleError(e, 'deletePopupById'));

// ── Global Site Settings ─────────────────────────────────────────────────────
const fetchSiteSettings = () =>
  api.get('/fb/site-settings').then(r => r.data).catch(e => handleError(e, 'fetchSiteSettings'));

const updateSiteSettings = (settings) =>
  api.put('/fb/site-settings', { settings }).then(r => r.data).catch(e => handleError(e, 'updateSiteSettings'));

const fetchTrustItems = () =>
  api.get('/fb/trust-icons').then(r => r.data).catch(e => handleError(e, 'fetchTrustItems'));

const createTrustItem = (data) =>
  api.post('/fb/trust-icons', data).then(r => r.data).catch(e => handleError(e, 'createTrustItem'));

const updateTrustItem = (id, data) =>
  api.put(`/fb/trust-icons/${id}`, data).then(r => r.data).catch(e => handleError(e, 'updateTrustItem'));

const deleteTrustItem = (id) =>
  api.delete(`/fb/trust-icons/${id}`).then(r => r.data).catch(e => handleError(e, 'deleteTrustItem'));

const initializeExperience = () => api.post('/fb/page-builder/initialize').then(r => r.data);

  return { fetchFbSections, updateFbSection, reorderFbSections, fetchHeroSlides, createHeroSlide, updateHeroSlide, deleteHeroSlide, reorderHeroSlides, fetchOccasions, createOccasion, updateOccasion, deleteOccasion, reorderOccasions, fetchVariants, createVariant, updateVariant, deleteVariant, reorderVariants, fetchJainSection, updateJainSection, fetchSteps, createStep, updateStep, deleteStep, reorderSteps, fetchGallery, createGalleryImage, updateGalleryImage, deleteGalleryImage, reorderGalleryImages, fetchUgcVideos, createUgcVideo, updateUgcVideo, deleteUgcVideo, reorderUgcVideos, fetchReviews, createReview, updateReview, deleteReview, reorderReviews, fetchFaqs, createFaq, updateFaq, deleteFaq, reorderFaqs, fetchFooter, updateFooterSettings, createFooterLink, updateFooterLink, deleteFooterLink, reorderFooterLinks, createFooterSocial, updateFooterSocial, deleteFooterSocial, fetchPopup, updatePopup, fetchPageBuilderModules, fetchCmsPages, fetchCmsPage, createCmsPage, updateCmsPage, deleteCmsPage, createCmsPageSection, updateCmsPageSection, deleteCmsPageSection, reorderCmsPageSections, uploadCmsImage, uploadCmsVideo, uploadVideoHeroMedia, createPreviewSession, fetchBlogs, createBlog, updateBlog, deleteBlog, fetchPopups, createPopup, updatePopupById, deletePopupById, fetchSiteSettings, updateSiteSettings, fetchTrustItems, createTrustItem, updateTrustItem, deleteTrustItem, initializeExperience };
};
export const { fetchFbSections, updateFbSection, reorderFbSections, fetchHeroSlides, createHeroSlide, updateHeroSlide, deleteHeroSlide, reorderHeroSlides, fetchOccasions, createOccasion, updateOccasion, deleteOccasion, reorderOccasions, fetchVariants, createVariant, updateVariant, deleteVariant, reorderVariants, fetchJainSection, updateJainSection, fetchSteps, createStep, updateStep, deleteStep, reorderSteps, fetchGallery, createGalleryImage, updateGalleryImage, deleteGalleryImage, reorderGalleryImages, fetchUgcVideos, createUgcVideo, updateUgcVideo, deleteUgcVideo, reorderUgcVideos, fetchReviews, createReview, updateReview, deleteReview, reorderReviews, fetchFaqs, createFaq, updateFaq, deleteFaq, reorderFaqs, fetchFooter, updateFooterSettings, createFooterLink, updateFooterLink, deleteFooterLink, reorderFooterLinks, createFooterSocial, updateFooterSocial, deleteFooterSocial, fetchPopup, updatePopup, fetchPageBuilderModules, fetchCmsPages, fetchCmsPage, createCmsPage, updateCmsPage, deleteCmsPage, createCmsPageSection, updateCmsPageSection, deleteCmsPageSection, reorderCmsPageSections, uploadCmsImage, uploadCmsVideo, uploadVideoHeroMedia, createPreviewSession, fetchBlogs, createBlog, updateBlog, deleteBlog, fetchPopups, createPopup, updatePopupById, deletePopupById, fetchSiteSettings, updateSiteSettings, fetchTrustItems, createTrustItem, updateTrustItem, deleteTrustItem, initializeExperience } = createStorefrontCmsService('');
export const useStorefrontCms = () => {
  const storefrontKey = experienceStorefrontKey();
  return useMemo(() => createStorefrontCmsService(storefrontKey), [storefrontKey]);
};
